import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase.js';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  doc, 
  setDoc, 
  deleteDoc,
  serverTimestamp
} from 'firebase/firestore';

export const useRecipeNotes = (userId, recipeId) => {
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [noteId, setNoteId] = useState(null);

  // Fetch the note for this recipe
  useEffect(() => {
    const fetchNote = async () => {
      if (!userId || !recipeId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const q = query(
          collection(db, 'userNotes'),
          where('userId', '==', userId),
          where('recipeId', '==', recipeId)
        );
        
        const querySnapshot = await getDocs(q);
        
        if (!querySnapshot.empty) {
          const noteDoc = querySnapshot.docs[0];
          setNote(noteDoc.data().noteText || '');
          setNoteId(noteDoc.id);
        } else {
          setNote('');
          setNoteId(null);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching note:', err);
        setError('Failed to load note');
      } finally {
        setLoading(false);
      }
    };

    fetchNote();
  }, [userId, recipeId]);

  // Save the note with debounce
  const saveNote = useCallback(async (noteText) => {
    if (!userId || !recipeId) return;
    
    try {
      setIsSaving(true);
      
      // If empty note and we have a noteId, delete the note
      if (!noteText.trim() && noteId) {
        await deleteDoc(doc(db, 'userNotes', noteId));
        setNoteId(null);
        setNote('');
        return;
      }
      
      // If empty note and no noteId, do nothing
      if (!noteText.trim() && !noteId) {
        return;
      }
      
      const noteData = {
        userId,
        recipeId,
        noteText,
        updatedAt: serverTimestamp()
      };
      
      if (noteId) {
        // Update existing note
        await setDoc(doc(db, 'userNotes', noteId), noteData, { merge: true });
      } else {
        // Create new note with auto-generated ID
        const newNoteRef = doc(collection(db, 'userNotes'));
        await setDoc(newNoteRef, noteData);
        setNoteId(newNoteRef.id);
      }
      
      setError(null);
    } catch (err) {
      console.error('Error saving note:', err);
      setError('Failed to save note');
    } finally {
      setIsSaving(false);
    }
  }, [userId, recipeId, noteId]);

  return {
    note,
    setNote,
    saveNote,
    loading,
    error,
    isSaving
  };
}; 